import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import MainLayout from '../components/layout/MainLayout';
import AppHeader from '../components/layout/AppHeader';
import MyRewardPageHeader from '../components/bazzar/MyRewardPageHeader';
import ReferralHowItWorks from '../components/referrals/ReferralHowItWorks';
import ReferralInvitePanel from '../components/referrals/ReferralInvitePanel';
import ReferralStatCard from '../components/referrals/ReferralStatCard';
import { referralsBgSrc } from '../assets/referrals';
import { fetchReferralData, shareReferralLink } from '../services/referralService';
import { showAppError } from '../services/messageDialogService';
import { clearAuthSession } from '../storage/authStorage';
import { BAZZAR_PATH } from '../routes/paths';

export default function ReferralsPage() {
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const loadReferral = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const data = await fetchReferralData();
      setReferral(data);
    } catch (err) {
      setReferral(null);
      setError(err?.message || 'Unable to load referral details.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReferral();
  }, [loadReferral]);

  const handleReferNow = async () => {
    if (!referral) return;

    try {
      const sharedNatively = await shareReferralLink(referral);
      if (!sharedNatively) {
        toast.success('Referral link copied!');
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      showAppError('Unable to share referral link.');
    }
  };

  const handleConfirmLogout = () => {
    clearAuthSession();
    window.location.href = '/';
  };

  return (
    <MainLayout>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          backgroundImage: `url(${referralsBgSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'left center',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ px: { md: '50px' } }}>
          <AppHeader endVariant="profile" onLogoutClick={() => setLogoutDialogOpen(true)} />
        </Box>

        <Box sx={{ px: { xs: 1.25, sm: 2, md: 3 }, pb: 6, pt: { xs: 1, md: 2 } }}>
          <MyRewardPageHeader title="Referrals" backLabel="Back to Home" backTo={BAZZAR_PATH} />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#ff00f2' }} />
            </Box>
          ) : error || !referral ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <Typography sx={{ color: '#e6d4ff', fontSize: 16, fontWeight: 600, textAlign: 'center' }}>
                {error || 'Unable to load referral details.'}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 2.5, md: 3.5 },
                width: '100%',
                maxWidth: 960,
                mx: 'auto',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
                  gap: { xs: 3, md: 4, lg: 5 },
                  alignItems: 'start',
                }}
              >
                <ReferralStatCard variant="rapcoins" value={referral.totalRapCoinsEarned} />
                <ReferralStatCard variant="referred" value={referral.totalReferred} />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
                  gap: { xs: 3, md: 4, lg: 5 },
                  alignItems: 'start',
                }}
              >
                <ReferralInvitePanel referral={referral} onReferNow={handleReferNow} />
                <ReferralHowItWorks steps={referral.howItWorksSteps} layout="side" />
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#1a0834',
            border: '1px solid #ff66f766',
            color: '#fff',
            minWidth: { xs: '90%', sm: 380 },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#fff' }}>Logout</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#e6d4ff' }}>Are you sure you want to logout?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLogoutDialogOpen(false)} sx={{ color: '#e6d4ff', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLogout}
            variant="contained"
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(90deg, #822AFF 0%, #FF00F2 100%)',
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
